/**
 * LinkTemplate — geração e acompanhamento dos links mágicos enviados ao cliente final.
 *
 * Migrado do conceito de `MonitoramentoLink` do simplerp (Fase 3.1). Só o essencial é editável
 * na criação (Template + dados do cliente) — `status`/`dados`/`linkmonitoramento`/
 * `linkrelatorio` são preenchidos pelo próprio fluxo público (`LinkController`), não pelo admin.
 */
import React, { Component } from 'react';
import { Tag } from 'primereact/tag';
import { TabPanel } from 'primereact/tabview';
import { TreeTable } from 'primereact/treetable';
import { Column } from 'primereact/column';
import { Message } from 'primereact/message';

import { TabViewComErro } from 'hay-core-react';
import { EntityGrid } from 'hay-core-react';
import { ModelTextInput } from 'hay-core-react';
import { ModelDropDown } from 'hay-core-react';
import { ModelUrl } from 'hay-core-react';
import { UtilService } from 'hay-core-react';
import Link from '../../../models/Link';
import TemplateModel from '../../../models/Template';
import LinkStatusEnum from '../../../enums/LinkStatusEnum';

// ─── Visualizador em árvore de `Link.dados` — mesmo vocabulário visual (ícone por tipo,
// formatação por tipo) do AuditoriaParametrosDialog (hay-core-react), mas embutido direto na
// aba do formulário (não num Dialog separado) e sem diff/restauro, que não fazem sentido aqui
// (não é um histórico de edição, é o resultado já persistido de um teste). ─────────────────────
function tipoDoValor(valor) {
  if (valor === null || valor === undefined) return 'nulo';
  if (typeof valor === 'boolean') return 'boolean';
  if (typeof valor === 'number') return 'number';
  if (Array.isArray(valor)) return 'array';
  if (typeof valor === 'object') return 'object';
  return 'string';
}
const ICONE_TIPO = { boolean: 'pi-check-square', number: 'pi-hashtag', string: 'pi-align-left', array: 'pi-list', object: 'pi-sitemap', nulo: 'pi-minus-circle' };

function montarNosJson(objeto, prefixo = '') {
  return Object.keys(objeto || {}).map((campo) => {
    const valor = objeto[campo];
    const tipo = tipoDoValor(valor);
    const key = `${prefixo}${campo}`;
    if (tipo === 'object' || tipo === 'array') {
      return { key, data: { campo, tipo }, children: montarNosJson(valor, `${key}.`) };
    }
    return { key, data: { campo, tipo, valor } };
  });
}

function formatarValorJson(valor, tipo) {
  if (tipo === 'nulo') return <span className="text-color-secondary font-italic">—</span>;
  if (tipo === 'boolean') return <i className={`pi ${valor ? 'pi-check text-green-500' : 'pi-times text-red-400'}`} />;
  if (tipo === 'number') return <span style={{ fontFamily: 'monospace' }}>{valor}</span>;
  const texto = String(valor);
  return (
    <span className="white-space-nowrap overflow-hidden text-overflow-ellipsis block" style={{ maxWidth: '28rem' }} title={texto}>
      {texto}
    </span>
  );
}

class VisualizadorDadosMonitoramento extends Component {
  render() {
    const { dados } = this.props;
    const nos = montarNosJson(dados);
    return (
      <TreeTable value={nos} scrollable scrollHeight="60vh" emptyMessage={UtilService.traduzir('main.link.dados.vazio')}>
        <Column
          field="campo"
          header={UtilService.traduzir('main.auditoria.parametros.campo')}
          expander
          body={(node) => (
            <span className="flex align-items-center gap-2">
              <i className={`pi ${ICONE_TIPO[node.data.tipo] || 'pi-circle'} text-color-secondary`} />
              <span className="font-semibold">{node.data.campo}</span>
            </span>
          )}
          style={{ minWidth: '20rem' }}
        />
        <Column
          header={UtilService.traduzir('main.auditoria.parametros.valor')}
          body={(node) => (node.children ? null : formatarValorJson(node.data.valor, node.data.tipo))}
          style={{ minWidth: '14rem' }}
        />
      </TreeTable>
    );
  }
}

// ─── Mapeamento status → Tag (mesmo padrão de hay-hotspot/UsuarioTransacaoTemplate.js) ────────
const STATUS_CONFIG = {
  'main.link.status.aguardandoinicio': { severity: 'warning', icon: 'pi-clock' },
  'main.link.status.iniciado': { severity: 'info', icon: 'pi-spin pi-spinner' },
  'main.link.status.finalizado': { severity: 'success', icon: 'pi-check-circle' },
};

// Colunas visíveis por padrão na grid: só Identificador/Template/Início/Fim da Coleta — o resto
// (status, nome/documento do cliente, links) fica disponível via "Configuração de Colunas", sem
// poluir a visão padrão.
const model = {
  identificador: { exibir: 'main.link.identificador', visivel: true, largura: '12rem' },
  status: { exibir: 'main.link.status', visivel: true, tipo: 'enum', entidade: 'link.status', template: 'status.template', disabled: true },
  monitoramentotemplate: {
    exibir: 'main.link.monitoramentotemplate',
    visivel: true,
    tipo: 'relacao',
    camporelacao: 'descricao',
    entidade: 'monitoramentotemplate.entidade',
    template: 'monitoramentotemplate.template',
    validacao: { obrigatorio: true },
  },
  nomecliente: { exibir: 'main.link.nomecliente' },
  documentocliente: { exibir: 'main.link.documentocliente' },
  identificadorterceiros: { exibir: 'main.link.identificadorterceiros' },
  linkmonitoramento: { exibir: 'main.link.linkmonitoramento', disabled: true, largura: '20rem' },
  linkrelatorio: { exibir: 'main.link.linkrelatorio', disabled: true, largura: '20rem' },
  // Sem coluna própria (`hidegrid`) — só precisa vir populado (`model.dados.value`/
  // `model.avaliacaoia.value`) pro formulário poder oferecer as abas abaixo (mesmo padrão de
  // `linkmonitoramento`).
  dados: { exibir: 'main.link.dados', hidegrid: true },
  avaliacaoia: { exibir: 'main.link.avaliacaoia', hidegrid: true, disabled: true },
  // tipo: 'date' (não os *formatado pré-calculados no backend) — sem valor, o grid já mostra
  // "Ainda não ocorreu" (main.naoOcorreu, ver EntityDataTable.js), mesmo mecanismo genérico usado
  // por qualquer outro campo data/hora (ex.: Usuario.ultimoacesso).
  iniciocoleta: { exibir: 'main.link.iniciocoleta', visivel: true, disabled: true, tipo: 'date', time: true },
  fimcoleta: { exibir: 'main.link.fimcoleta', visivel: true, disabled: true, tipo: 'date', time: true },
};

const renderers = {
  'link.status': LinkStatusEnum,
  'monitoramentotemplate.entidade': TemplateModel,
  'monitoramentotemplate.template': (rowData) => rowData.monitoramentotemplate?.descricao || '',
  status: {
    template: (rowData) => {
      const cfg = STATUS_CONFIG[rowData.status] || { severity: 'secondary', icon: 'pi-question-circle' };
      return (
        <Tag
          severity={cfg.severity}
          icon={`pi ${cfg.icon}`}
          value={UtilService.traduzir(rowData.status)}
          title={UtilService.traduzir(rowData.status + '.help')}
          className="white-space-nowrap"
        />
      );
    },
  },
};

class LinkFormulario extends Component {
  render() {
    const { model, contexto } = this.props;
    const temDados = model.dados?.value && Object.keys(model.dados.value).length > 0;
    return (
      <TabViewComErro tentativaSalvar={contexto.state.tentativaSalvar} className="col-12 mt-2">
        <TabPanel header={UtilService.traduzir('main.link.aba.principal')} leftIcon="pi pi-home mr-2">
          <div className="grid w-full">
            <div className="field col-12 mt-3"><ModelDropDown model={model.monitoramentotemplate} contexto={contexto} /></div>
            <div className="field col-12 md:col-6 mt-3"><ModelTextInput model={model.nomecliente} contexto={contexto} /></div>
            <div className="field col-12 md:col-6 mt-3"><ModelTextInput model={model.documentocliente} contexto={contexto} /></div>
            <div className="field col-12 mt-3"><ModelTextInput model={model.identificadorterceiros} contexto={contexto} /></div>
            {model.linkmonitoramento?.value && (
              <div className="field col-12 mt-3"><ModelUrl model={model.linkmonitoramento} contexto={contexto} /></div>
            )}
            {model.linkrelatorio?.value && (
              <div className="field col-12 mt-3"><ModelUrl model={model.linkrelatorio} contexto={contexto} /></div>
            )}
          </div>
        </TabPanel>
        <TabPanel header={UtilService.traduzir('main.link.aba.dados')} leftIcon="pi pi-database mr-2" disabled={!model.id?.value}>
          {temDados ? (
            <VisualizadorDadosMonitoramento dados={model.dados.value} />
          ) : (
            <Message severity="secondary" className="w-full" text={UtilService.traduzir('main.link.dados.vazio')} />
          )}
        </TabPanel>
        <TabPanel header={UtilService.traduzir('main.link.aba.avaliacaoia')} leftIcon="pi pi-sparkles mr-2" disabled={!model.id?.value}>
          {model.avaliacaoia?.value ? (
            <div className="p-3 border-round surface-50 line-height-3 white-space-pre-wrap">
              {model.avaliacaoia.value}
            </div>
          ) : (
            <Message severity="secondary" className="w-full" text={UtilService.traduzir('main.link.avaliacaoia.vazio')} />
          )}
        </TabPanel>
      </TabViewComErro>
    );
  }
}

class LinkTemplate extends Component {
  render() {
    return (
      <EntityGrid
        entidade={Link}
        model={model}
        identidade="main.link"
        permissoes={this.props.permissoes}
        icone="pi-share-alt"
        storageKey="dt-links"
        renderers={renderers}
        // status não aparece no formulário de criação (LinkFormulario não renderiza
        // model.status — é preenchido pelo próprio fluxo público, ver comentário no topo do
        // arquivo), mas por estar declarado no schema (necessário pra coluna da grid),
        // UtilService.extrairModel ainda manda a chave no create — sem valor selecionado, um
        // campo enum manda `null` explícito (comportamento genérico, necessário pra permitir
        // limpar um enum na edição). `Link.status` não aceita `null` (type: 'string', sem
        // allowNull) — só teria efeito real se `status` fosse editável na criação, o que não é
        // o caso aqui, então só precisamos garantir que o valor enviado seja válido.
        valoresPadraoNovo={{ status: 'main.link.status.aguardandoinicio' }}
        configuracao={this.props.configuracao}
        navigate={this.props.navigate}
        location={this.props.location}
        searchparams={this.props.searchparams}
        formulario={(m, ctx) => <LinkFormulario model={m} contexto={ctx} />}
      />
    );
  }
}

export default LinkTemplate;
